import { DataTexture, FloatType, RGBAFormat, Vector3 } from 'three';
import { Point3D } from '../../../team-apis/otterdam/answers/shape-typings';

export const pointMotionCompensated = `
vec3 fetchCuboidComponent(int index, int offset) {
  return texelFetch(compensatedCuboids, ivec2(offset, index), 0).rgb;
}

bool isInsideAnyCuboid(vec3 h3Position, int index){
  vec3 c0 = fetchCuboidComponent(index, 0);
  vec3 c1 = fetchCuboidComponent(index, 1);
  vec3 c2 = fetchCuboidComponent(index, 2);
  vec3 c3 = fetchCuboidComponent(index, 3);

  vec3 p14 = vec3(c0 - c2);
  vec3 p12 = vec3(c0 - c1);
  vec3 p15 = vec3(c0 - c3);

  vec3 u = cross(p14,p15);
  vec3 v = cross(p12,p15);
  vec3 w = cross(p12,p14);

  float x = dot(h3Position, u);
  float y = dot(h3Position, v);
  float z = dot(h3Position, w);

  bool isX = min(dot(u, c0), dot(u, c1)) < x  && x < max(dot(u, c0), dot(u, c1));
  bool isY = min(dot(v, c0), dot(v, c2)) < y && y < max(dot(v, c0), dot(v, c2));
  bool isZ = min(dot(w, c0), dot(w, c3)) < z && z < max(dot(w, c0), dot(w, c3));
  return isX && isY && isZ;
}

vec3 getPointMotionCompensatedPosition(vec3 h3Position) {
  for(int index = 0; index < compensatedCuboidCount; index++) {
    if(isInsideAnyCuboid(h3Position, index)) {
      vec4 offset = vec4(fetchCuboidComponent(index, 4), 0);
      vec4 vertexPosition = modelMatrix * vec4(h3Position, 1.0);
      vec4 offsetPosition = vertexPosition + vec4(offset.x, offset.y, offset.z, 0.0);
      vec4 localPosition = inverse(modelMatrix) * offsetPosition;
      localPosition = localPosition / localPosition.w;
      return vec3(localPosition);
    }
  }

  return h3Position;
}
`;

export type EgoMotionCompensationParams = {
  compensatedCuboids: DataTexture;
  compensatedCuboidCount: number;
};

export type KeyLocationWithTime = {
  vertices: Vector3[];
  center: Point3D;
  timestamp: number;
};

export type KeyLocationVelocity = {
  cuboid: Vector3[];
  offset: Point3D;
};

export function getEgoMotionCompensationTexture(
  velocities: KeyLocationVelocity[],
  outputName: string,
) {
  let data = new Float32Array(20);
  let height = 1;
  if (velocities.length > 0) {
    data = buildCompensationFloat32Array(velocities);
    height = velocities.length;
  }

  const lookUpTexture = new DataTexture(data, 5, height, RGBAFormat, FloatType);
  lookUpTexture.needsUpdate = true;
  lookUpTexture.name = `ego-motion-compensation-${outputName}`;

  return lookUpTexture;
}

function buildCompensationFloat32Array(velocities: KeyLocationVelocity[]) {
  const componentCount = 4;
  const height = 5 * componentCount;
  const width = velocities.length;
  const data = new Float32Array(width * height);

  let index = 0;
  for (const velocity of velocities) {
    for (const vertices of velocity.cuboid) {
      const { x, y, z } = vertices;
      const row = [x, y, z, 0];
      data.set(row, index);
      index += row.length;
    }
    const offset = [...velocity.offset, 0];
    data.set(offset, index);
    index += offset.length;
  }

  return data;
}
